import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  PRODUKTARTNAME,
  type Artikelstatus,
  type Artikelzeile,
  type Produktart,
  type Produktstandort,
  liste,
} from '../api/produkte';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { Feldraster, Feldzeile } from '../komponenten/Akte';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/** Die Trefferliste beginnt bei drei Zeichen — wie bei den Kunden (KUN-22). */
const AB_ZEICHEN = 3;

type Filter = {
  readonly suche: string;
  readonly produktart: Produktart | '';
  readonly status: Artikelstatus | '';
  readonly nurWaehlbar: boolean;
};

const LEER: Filter = { suche: '', produktart: '', status: '', nurWaehlbar: false };

/**
 * Die Standorte eines Produkts als eine Zeile (PRD-01 i. d. F. AeA-39,
 * Entwurf, Auftrag #22); ein abgeschalteter sagt es. `null`, wenn es keine gibt.
 */
function standortnamen(standorte: readonly Produktstandort[]): string | null {
  if (standorte.length === 0) return null;
  return standorte
    .map((s) => (s.aktiv ? s.bezeichnung : `${s.bezeichnung} (abgeschaltet)`))
    .join(', ');
}

/**
 * Die Artikelliste — der Einstieg in den Bereich „Produkte".
 *
 * Seitentyp Liste (ÄA-41), derselbe Baustein wie bei den Kunden (M-10):
 * Zähler und „Neuer Artikel" im Seitenkopf, darunter die Filterleiste mit
 * abwählbaren Marken, die Rastertabelle und die Vorschau ohne Seitenwechsel.
 * Das ist keine Bequemlichkeit — dasselbe Personal bedient beide Listen, und
 * zwei Muster fuer dieselbe Handlung kosten jeden Tag ein paar Sekunden
 * Nachdenken.
 *
 * DREI DINGE SIND HIER ANDERS ALS BEI DEN KUNDEN, und jedes hat einen Grund
 * im Lastenheft:
 *
 *   * Die SPALTE „waehlbar" statt eines stillen Filters. PRD-03 sagt:
 *     gesperrte Artikel sind in neuen Belegen nicht waehlbar, bleiben aber
 *     sichtbar. Wer sie ausblendete, liesse den Anwender denselben Artikel
 *     ein zweites Mal anlegen.
 *   * Der wirksame STEUERSCHLUESSEL steht in der Liste, obwohl er meist
 *     geerbt ist (PRD-02). Er ist die haeufigste Ursache eines falschen
 *     Belegs, und in der Liste sieht man die Ausreisser nebeneinander.
 *   * Die PRODUKTART als Marke. Sie bestimmt nach PRD-28 Pflichtfelder,
 *     Planungsregeln und Belegverhalten — sie ist kein Etikett.
 */
export function Artikelliste({
  beiOeffnen,
  beiNeu,
}: {
  readonly beiOeffnen: (id: string) => void;
  readonly beiNeu: (bezeichnung: string) => void;
}): JSX.Element {
  const [filter, setzeFilter] = useState<Filter>(LEER);
  const [artikel, setzeArtikel] = useState<readonly Artikelzeile[]>([]);
  const [mehr, setzeMehr] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [vorschau, setzeVorschau] = useState<Artikelzeile | undefined>(undefined);

  const laden = useCallback(async (f: Filter): Promise<void> => {
    setzeLaedt(true);
    try {
      const ergebnis = await liste({
        suche: f.suche.length >= AB_ZEICHEN ? f.suche : '',
        produktart: f.produktart,
        status: f.status,
        nurWaehlbar: f.nurWaehlbar,
      });
      setzeArtikel(ergebnis.artikel);
      setzeMehr(ergebnis.mehrAlsGezeigt);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  // 250 Millisekunden Ruhe genuegen, damit ein Wort zu Ende getippt ist.
  useEffect(() => {
    const kennung = setTimeout(() => void laden(filter), 250);
    return () => clearTimeout(kennung);
  }, [filter, laden]);

  const marken: readonly Filtermarke[] = [
    ...(filter.produktart !== ''
      ? [
          {
            text: `Art: ${PRODUKTARTNAME[filter.produktart]}`,
            entfernen: () => setzeFilter((f) => ({ ...f, produktart: '' })),
          },
        ]
      : []),
    ...(filter.status !== ''
      ? [
          {
            text: `Status: ${filter.status}`,
            entfernen: () => setzeFilter((f) => ({ ...f, status: '' })),
          },
        ]
      : []),
    ...(filter.nurWaehlbar
      ? [
          {
            text: 'nur wählbare',
            entfernen: () => setzeFilter((f) => ({ ...f, nurWaehlbar: false })),
          },
        ]
      : []),
  ];

  const zuKurz = filter.suche !== '' && filter.suche.length < AB_ZEICHEN;

  /* ÄA-41: Der Zähler steht im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(artikel.length, 'Artikel', 'Artikel', mehr));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {/*
        ÄA-41, Seitentyp Liste: „Neuer Artikel" steht im Seitenkopf
        (Arbeitsfläche) — dort, wo jede Liste ihre Haupthandlung trägt. Der
        Knopf in der Leiste entfällt dafür; im Leerzustand bleibt die
        Neuanlage, mit dem Suchbegriff, wo es einen gibt.
      */}
      <Liste
        suche={{
          wert: filter.suche,
          aendern: (wert) => setzeFilter((f) => ({ ...f, suche: wert })),
          beschriftung: 'Artikel suchen',
          platzhalter: 'Bezeichnung oder Artikelnummer',
          beschrieben: 'artikel-suchhilfe',
        }}
        filter={
          <>
            <select
              className="liste__filter"
              value={filter.produktart}
              onChange={(e) =>
                setzeFilter((f) => ({ ...f, produktart: e.target.value as Produktart | '' }))
              }
              aria-label="Produktart"
            >
              <option value="">Alle Produktarten</option>
              {(Object.keys(PRODUKTARTNAME) as Produktart[]).map((a) => (
                <option key={a} value={a}>
                  {PRODUKTARTNAME[a]}
                </option>
              ))}
            </select>
            <select
              className="liste__filter"
              value={filter.status}
              onChange={(e) =>
                setzeFilter((f) => ({ ...f, status: e.target.value as Artikelstatus | '' }))
              }
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              <option value="aktiv">aktiv</option>
              <option value="auslaufend">auslaufend</option>
              <option value="gesperrt">gesperrt</option>
            </select>
            {/* PRD-03 */}
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={filter.nurWaehlbar}
                onChange={(e) => setzeFilter((f) => ({ ...f, nurWaehlbar: e.target.checked }))}
              />
              nur wählbare
            </label>
          </>
        }
        marken={marken}
        hinweise={
          <>
            <p className="hilfe" id="artikel-suchhilfe">
              Die Suche findet ab drei Zeichen und verzeiht Tippfehler. Eine Artikelnummer wird
              dagegen genau genommen — sie ist eine Kennung und nie ungefähr richtig.
            </p>
            {zuKurz && (
              <p role="status">Noch {AB_ZEICHEN - filter.suche.length} Zeichen bis zur Suche.</p>
            )}
            {mehr && (
              <p className="warnung" role="status">
                Mehr als 500 Treffer. Schränken Sie die Suche ein — die Liste zeigt die ersten 500.
              </p>
            )}
          </>
        }
        vorschau={
          vorschau === undefined ? (
            <p className="feld__hilfe">
              Fahren Sie über eine Zeile — die Vorschau erspart den Sprung in den Datensatz, wenn
              nur eine Auskunft gefragt ist.
            </p>
          ) : (
            <>
              <div className="karte__titel">
                {vorschau.bezeichnung}
                <small>{vorschau.artikelnummer}</small>
              </div>
              <Feldraster>
                <Feldzeile beschriftung="Produktart" wert={PRODUKTARTNAME[vorschau.produktart]} />
                <Feldzeile beschriftung="Warengruppe" wert={vorschau.warengruppenname} />
                <Feldzeile beschriftung="Mengeneinheit" wert={vorschau.mengeneinheit} />
                {vorschau.standorte.length > 0 && (
                  <Feldzeile beschriftung="Standorte" wert={standortnamen(vorschau.standorte)} />
                )}
                {/* PRD-02: der wirksame Schlüssel, auch wenn er geerbt ist. */}
                <Feldzeile
                  beschriftung="Steuerschlüssel"
                  wert={vorschau.steuerschluessel}
                  anzeige={vorschau.steuerschluessel ?? '— nicht gesetzt'}
                />
              </Feldraster>
              {!vorschau.waehlbar && (
                <p className="warnung">
                  Dieser Artikel ist in neuen Belegen nicht wählbar. In alten Belegen bleibt er
                  sichtbar.
                </p>
              )}
              {vorschau.steuerschluessel === null && (
                <p className="warnung">
                  Ohne Steuerschlüssel lässt sich kein Preis berechnen — weder am Artikel noch in
                  seiner Warengruppe ist einer gesetzt.
                </p>
              )}
              <div className="knopfreihe">
                <button type="button" className="knopf" onClick={() => beiOeffnen(vorschau.id)}>
                  Akte öffnen
                </button>
              </div>
            </>
          )
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">
              {artikel.length} Artikel
              {filter.suche.length >= AB_ZEICHEN ? ', nach Treffergüte sortiert' : ''}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="liste__spalte--knapp">
                  Nummer
                </th>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Art</th>
                {/* PRD-01 i. d. F. AeA-39 (Entwurf, Auftrag #22) */}
                <th scope="col">Standorte</th>
                <th scope="col">Einheit</th>
                <th scope="col">Steuer</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {artikel.map((a) => (
                <tr
                  key={a.id}
                  className={vorschau?.id === a.id ? 'liste__zeile--gewaehlt' : undefined}
                  onMouseEnter={() => setzeVorschau(a)}
                  onFocus={() => setzeVorschau(a)}
                >
                  <td className="liste__spalte--knapp">
                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiOeffnen(a.id)}>
                      {a.artikelnummer}
                    </button>
                  </td>
                  <td className="liste__name">{a.bezeichnung}</td>
                  <td>{PRODUKTARTNAME[a.produktart]}</td>
                  <td>{standortnamen(a.standorte) ?? '—'}</td>
                  <td>{a.mengeneinheit}</td>
                  <td>{a.steuerschluessel ?? '—'}</td>
                  <td>
                    {/* WCAG 1.4.1: Der Zustand steht als Text da, nicht als Farbe. */}
                    <span className={markenklasse(a.status === 'aktiv' ? 'laufend' : 'stillgelegt')}>
                      {a.status}
                    </span>
                    {/* PRD-03 */}
                    {!a.waehlbar && <span className="marke marke--gesperrt">nicht wählbar</span>}
                    {/* BUC-11 nach ÄA-55 */}
                    {a.warteliste === true && <span className="marke">Warteliste</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && artikel.length === 0 && (
          <div className="liste__leer" role="status">
            {filter.suche.length >= AB_ZEICHEN ? (
              <>
                <p>Kein Treffer für „{filter.suche}“.</p>
                <button
                  type="button"
                  className="knopf knopf--haupt"
                  onClick={() => beiNeu(filter.suche)}
                >
                  „{filter.suche}“ neu anlegen
                </button>
              </>
            ) : (
              <>
                <p>Suchen Sie nach Bezeichnung oder Artikelnummer.</p>
                <button type="button" className="knopf knopf--haupt" onClick={() => beiNeu('')}>
                  Artikel anlegen
                </button>
              </>
            )}
          </div>
        )}
      </Liste>
    </>
  );
}
